const REDACTION_MARKER = '[REDACTED]'
const REDACTION_MARKER_RE = /\[REDACTED(?:_[A-Z0-9_]+)?\]/gi

// Keep the browser-side projection deliberately small. The server remains the
// trust boundary, while this helper prevents an older/raw device response from
// being rendered or copied by the admin UI during a rolling deployment.
const SECRET_NAMES = [
  'replay[-_ ]?ingest[-_ ]?token',
  'auth[-_ ]?token',
  'access[-_ ]?token',
  'refresh[-_ ]?token',
  'stream[-_ ]?key',
  'api[-_ ]?key',
  'auth[-_ ]?key',
  'password', 'passwd', 'pass', 'pwd', 'psw', 'secret', 'token', 'key', 'auth'
].join('|')

function decodePercentRound(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    // A malformed escape elsewhere in the line must not prevent valid encoded
    // runs from being inspected.
    return value.replace(/(?:%[0-9a-f]{2})+/gi, run => {
      try {
        return decodeURIComponent(run)
      } catch {
        return run
      }
    })
  }
}

function decodeForInspection(value: string): string {
  let decoded = value
  for (let i = 0; i < 2; i += 1) {
    const next = decodePercentRound(decoded)
    if (next === decoded) break
    decoded = next
  }
  return decoded
}

function redactRecognizedText(value: string): string {
  // URL userinfo: retain the username and host/path, replace only the password.
  let redacted = value.replace(
    /(\b[a-z][a-z0-9+.-]*:\/\/[^/\s:@]+:)[^@\s/]+(@)/gi,
    `$1${REDACTION_MARKER}$2`
  )

  // Quoted assignments, including shell/JSON-like forms.
  redacted = redacted.replace(
    new RegExp(`(^|[^A-Za-z0-9])((?:${SECRET_NAMES})\\s*[=:_]\\s*)"[^"]*"`, 'gim'),
    `$1$2"${REDACTION_MARKER}"`
  )
  redacted = redacted.replace(
    new RegExp(`(^|[^A-Za-z0-9])((?:${SECRET_NAMES})\\s*[=:_]\\s*)'[^']*'`, 'gim'),
    `$1$2'${REDACTION_MARKER}'`
  )

  // Unquoted values stop before the next `_name=...` style parameter. This is
  // important for XMeye URLs: keep `_channel=0_stream=0` readable.
  redacted = redacted.replace(
    new RegExp(
      `(^|[^A-Za-z0-9])((?:${SECRET_NAMES})\\s*[=:_]\\s*)(?!["'])` +
      `([^\\s,;"'&?#]+?)(?=(?:_[A-Za-z][A-Za-z0-9-]*\\s*[=:_])|[\\s,;"'&?#}]|$)`,
      'gim'
    ),
    `$1$2${REDACTION_MARKER}`
  )

  redacted = redacted
    .replace(/((?:Bearer|Basic)\s+)[A-Za-z0-9+/=._~-]+/gi, `$1${REDACTION_MARKER}`)
    .replace(/\btskey-[A-Za-z0-9_-]+\b/gi, REDACTION_MARKER)

  return redacted
}

/** Redact secrets from text before it is rendered, copied or placed in a toast. */
export function redactSecretText(value?: string | null): string {
  if (!value) return ''

  // Mask before decoding so an encoded delimiter inside a value (for example
  // PASS=left%26right) cannot become a boundary and expose the decoded tail.
  // Then inspect two bounded decode rounds for encoded names/separators.
  let redacted = redactRecognizedText(String(value))
  for (let i = 0; i < 2; i += 1) {
    const decoded = decodePercentRound(redacted)
    redacted = redactRecognizedText(decoded)
    if (decoded === redacted && !/%[0-9a-f]{2}/i.test(decoded)) break
  }
  return redacted
}

export function containsRedactedSecret(value?: string | null): boolean {
  if (!value) return false
  REDACTION_MARKER_RE.lastIndex = 0
  return REDACTION_MARKER_RE.test(decodeForInspection(value))
}

/**
 * Materialise a masked probe URL from the password the operator just entered.
 * Returns null rather than allowing a redaction marker to be persisted.
 */
export function restoreRedactedSecret(value: string, secret?: string | null): string | null {
  if (!containsRedactedSecret(value)) return value
  if (!secret) return null
  const decoded = decodeForInspection(value)
  REDACTION_MARKER_RE.lastIndex = 0
  const restored = decoded.replace(REDACTION_MARKER_RE, secret)
  return containsRedactedSecret(restored) ? null : restored
}
